import { useState } from "react";
import { Link, useSearch } from "wouter";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { SENHA_MINIMA } from "@shared/schema";

/**
 * `/definir-senha?token=…` — PÚBLICA. Serve ao convite (7 dias) e ao reset (24 h):
 * a pessoa escolhe a senha e vai para o login. Não loga sozinha, de propósito.
 */
export default function DefinirSenhaPage() {
  const search = useSearch();
  const token = new URLSearchParams(search).get("token") ?? "";
  const [senha, setSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const invalida = senha.length < SENHA_MINIMA || senha !== confirma;

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await apiRequest("POST", "/api/auth/definir-senha", { token, senha });
      setPronto(true);
    } catch (err: any) {
      setErro(err?.mensagem ?? "Este link não é mais válido. Peça um novo ao administrador.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Definir senha</CardTitle>
          <CardDescription>Escolha a senha do seu acesso ao Rizoma Farm.</CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <p className="text-sm text-destructive" data-testid="texto-sem-token">
              Este link está incompleto. Abra o link exatamente como foi enviado, ou peça um novo.
            </p>
          ) : pronto ? (
            <div className="space-y-4 text-center" data-testid="senha-definida">
              <CheckCircle2 className="h-10 w-10 mx-auto text-sucesso" />
              <p className="text-sm">Senha definida. Agora é só entrar.</p>
              <Button asChild className="w-full"><Link href="/login">Ir para o login</Link></Button>
            </div>
          ) : (
            <form onSubmit={enviar} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="senha">Nova senha</Label>
                <Input id="senha" type="password" autoComplete="new-password" value={senha} onChange={e => setSenha(e.target.value)} data-testid="input-nova-senha" />
                <p className="text-xs text-muted-foreground">Mínimo de {SENHA_MINIMA} caracteres.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirma">Repita a senha</Label>
                <Input id="confirma" type="password" autoComplete="new-password" value={confirma} onChange={e => setConfirma(e.target.value)} data-testid="input-confirma-senha" />
                {confirma && senha !== confirma && <p className="text-xs text-destructive">As senhas não conferem.</p>}
              </div>
              {erro && <p className="text-sm text-destructive" data-testid="erro-definir-senha">{erro}</p>}
              <Button type="submit" className="w-full" disabled={invalida || salvando} data-testid="button-definir-senha">
                {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar senha
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
